import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';
import { appLinkPath, appUrl } from '@/lib/appUrl';
import { useAuth } from '@/contexts/AuthContext';
import { syncMvpReminders } from '@/lib/nativeNotifications';
import { registerNativePush } from '@/lib/nativePush';

export function NativeAppEvents() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const { user, loading } = useAuth();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let active = true;
    const open = ({ url }) => {
      const path = appLinkPath(url);
      if (active && path) navigateRef.current(path);
    };
    const handles = [
      App.addListener('appUrlOpen', open),
      LocalNotifications.addListener('localNotificationActionPerformed', ({ notification }) => {
        if (notification.extra?.path) open({ url: appUrl(notification.extra.path) });
      }),
    ];
    App.getLaunchUrl().then(value => { if (value) open(value); }).catch(console.error);
    return () => {
      active = false;
      handles.forEach(h => h.then(listener => listener.remove()).catch(console.error));
    };
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || loading) return;
    let active = true;
    let syncing = false;
    const sync = async () => {
      if (syncing || !active) return;
      syncing = true;
      try { await syncMvpReminders(user?.user_id, () => active); }
      catch (e) { console.error('MVP reminder sync failed', e); }
      finally { syncing = false; }
    };
    sync();
    const timer = setInterval(sync, 30000);
    const handle = App.addListener('appStateChange', ({ isActive }) => { if (isActive) sync(); });
    window.addEventListener('mvp-reminders-changed', sync);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener('mvp-reminders-changed', sync);
      handle.then(listener => listener.remove()).catch(console.error);
    };
  }, [user?.user_id, loading]);
  // Remote push: only once a user is known, so the token is tied to an account.
  // Retried on resume, because permission is often granted after first launch.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || loading || !user?.user_id) return;
    let cleanup = () => {};
    let cancelled = false;
    const attempt = () => {
      registerNativePush((path) => navigateRef.current(appLinkPath(appUrl(path)) || path))
        .then((off) => {
          if (cancelled) off();
          else cleanup = off;
        })
        .catch((e) => console.error('native push setup failed', e));
    };
    attempt();
    const resumed = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive && !cancelled) attempt();
    });
    // Fired by the bell panel right after the user allows notifications.
    window.addEventListener('mvp-reminders-changed', attempt);
    return () => {
      cancelled = true;
      window.removeEventListener('mvp-reminders-changed', attempt);
      resumed.then((listener) => listener.remove()).catch(console.error);
      cleanup();
    };
  }, [user?.user_id, loading]);

  return null;
}
